/**
 * resumableUpload — TUS v1.0.0 client for Supabase Storage.
 *
 * TUS is Supabase Storage's native resumable-upload protocol.
 * It lets us upload large files (RAW, ProRes, 4K/8K video) in
 * chunks, resume from where we left off if the network drops,
 * and blow past the 100MB PUT limit imposed by the standard
 * `.storage.upload()` call.
 *
 * We implement the protocol by hand with fetch rather than
 * pulling in tus-js-client (~50KB gzipped) — TUS is small, we
 * only need the create + patch pieces, and controlling the
 * primitive means we can wire progress + retry to the same
 * shape as `parallelUpload`.
 *
 * Protocol (TUS v1.0.0 — https://tus.io/protocols/resumable-upload):
 *   1. POST /storage/v1/upload/resumable
 *        headers: Tus-Resumable: 1.0.0
 *                 Upload-Length: <bytes>
 *                 Upload-Metadata: <base64 kv pairs>
 *                 Authorization: Bearer <jwt>
 *        response: 201 with Location: <resource url>
 *   2. PATCH <resource url>
 *        headers: Tus-Resumable: 1.0.0
 *                 Upload-Offset: <current offset>
 *                 Content-Type: application/offset+octet-stream
 *        body:    <chunk bytes>
 *        response: 204 with Upload-Offset: <new offset>
 *   3. Repeat step 2 until Upload-Offset === Upload-Length.
 *
 * Resume:
 *   HEAD <resource url> returns Upload-Offset — pick up from
 *   there.  We don't persist the resource URL across page
 *   reloads today (memory only), so resume is limited to
 *   in-session recovery from a chunk failure.  Full cross-
 *   session resume is a Wave 2 add.
 */
import { supabase } from '@/integrations/supabase/client';

const TUS_ENDPOINT_PATH = '/storage/v1/upload/resumable';
/** 6 MB per chunk — Supabase's recommended chunk size. */
const CHUNK_SIZE = 6 * 1024 * 1024;

export interface ResumableUploadArgs {
  bucket: string;
  path: string;
  file: File;
  /** Fires with a 0…1 fraction as chunks complete. */
  onProgress?: (fraction: number) => void;
  /** Abort signal — cancels in-flight chunks + stops retries. */
  signal?: AbortSignal;
  /**
   * Overwrite an existing object at `path` when true. Supabase's
   * TUS layer honours this via the `upsert` Upload-Metadata field;
   * without it a re-upload of the same path fails with "Object
   * already exists". Galleries paths carry a timestamp so they
   * never collide (default `false` stays safest for them);
   * Deliveries reuse the raw file name and rely on this flag to
   * replace the previous copy on retry.
   */
  upsert?: boolean;
  /**
   * Additional Upload-Metadata fields — e.g. `cacheControl`. Merged
   * over the defaults `resumableUpload` sets. Optional; callers
   * that only need default caching can omit it.
   */
  metadata?: Record<string, string>;
}

export interface ResumableUploadResult {
  path: string;
  url: string;
}

/** Base64-encode a string for the TUS Upload-Metadata header. */
function b64(s: string): string {
  // TUS spec requires ASCII-safe base64 (no url-encoding).
  return btoa(unescape(encodeURIComponent(s)));
}

/** Compose the Upload-Metadata header value from a kv record. */
function metadataHeader(kv: Record<string, string>): string {
  return Object.entries(kv)
    .map(([k, v]) => `${k} ${b64(v)}`)
    .join(',');
}

async function getAuthToken(): Promise<string> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error('Not signed in');
  return token;
}

/** Base URL for the current Supabase project.  Derived from the
 *  storage.from() URL builder so we don't hard-code the project id. */
function projectBaseUrl(): string {
  // Supabase client exposes `supabase.storage.url` in newer SDK
  // versions; fall back to the storage builder for older ones.
  const anyStorage = supabase.storage as unknown as { url?: string };
  if (anyStorage.url) return anyStorage.url.replace(/\/storage\/v1\/?$/, '');
  const testUrl = supabase.storage.from('_').getPublicUrl('_');
  // testUrl.data.publicUrl looks like:
  //   https://<project>.supabase.co/storage/v1/object/public/_/_
  const url = new URL(testUrl.data.publicUrl);
  return `${url.protocol}//${url.host}`;
}

export async function resumableUpload(
  args: ResumableUploadArgs,
): Promise<ResumableUploadResult> {
  const { bucket, path, file, onProgress, signal, upsert, metadata } = args;
  if (signal?.aborted) throw new Error('Upload aborted');

  const token = await getAuthToken();
  const base = projectBaseUrl();
  const createUrl = `${base}${TUS_ENDPOINT_PATH}`;

  // ── 1. Create the upload ────────────────────────────────────
  const createRes = await fetch(createUrl, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Tus-Resumable': '1.0.0',
      'Upload-Length': String(file.size),
      // Supabase's TUS server reads the `upsert` value from the
      // Upload-Metadata payload rather than the `x-upsert` header
      // that the JS SDK uses for single-PUT.
      'Upload-Metadata': metadataHeader({
        bucketName: bucket,
        objectName: path,
        contentType: file.type || 'application/octet-stream',
        cacheControl: '3600',
        ...(upsert ? { upsert: 'true' } : {}),
        ...(metadata ?? {}),
      }),
    },
    signal,
  });
  if (!createRes.ok) {
    throw new Error(
      `Resumable create failed: HTTP ${createRes.status} ${await createRes.text().catch(() => '')}`.trim(),
    );
  }
  const resourceUrl = createRes.headers.get('Location');
  if (!resourceUrl) {
    throw new Error('Resumable create returned no Location header');
  }
  // Some setups return a relative URL — absolute-ify it.
  const absoluteUrl = resourceUrl.startsWith('http')
    ? resourceUrl
    : `${base}${resourceUrl}`;

  // ── 2. PATCH chunks ─────────────────────────────────────────
  let offset = 0;
  while (offset < file.size) {
    if (signal?.aborted) throw new Error('Upload aborted');
    const end = Math.min(offset + CHUNK_SIZE, file.size);
    const chunk = file.slice(offset, end);
    const patchRes = await fetch(absoluteUrl, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Tus-Resumable': '1.0.0',
        'Upload-Offset': String(offset),
        'Content-Type': 'application/offset+octet-stream',
      },
      body: chunk,
      signal,
    });
    if (!patchRes.ok) {
      // On a 5xx we could HEAD the resource and resume from
      // the reported offset.  MVP: bubble up and let the outer
      // retry-with-backoff (in parallelUpload) reissue the whole
      // upload from scratch — TUS create with the same path is
      // idempotent enough for now.
      throw new Error(
        `Resumable chunk failed at offset ${offset}: HTTP ${patchRes.status}`,
      );
    }
    const newOffset = Number(patchRes.headers.get('Upload-Offset') ?? end);
    if (Number.isNaN(newOffset)) {
      throw new Error('Resumable chunk returned no Upload-Offset header');
    }
    offset = newOffset;
    onProgress?.(offset / file.size);
  }

  // ── 3. Build the public URL for the caller ──────────────────
  const { data: urlData } = supabase.storage.from(bucket).getPublicUrl(path);
  return { path, url: urlData.publicUrl };
}
